/**
 * Store 模組 - 輕量級響應式狀態管理
 *
 * 提供類似 Redux 的狀態管理，但更輕量：
 * - 使用 Pub/Sub 模式通知狀態變更
 * - 支援部分更新（partial update）
 * - 完整的 TypeScript 型別支援
 */

// ========================================================================
// 型別定義
// ========================================================================

/** 狀態監聽器函式 */
export type Listener<T> = (state: T, prevState: T) => void;

/** 選擇器函式 - 從狀態中選取部分資料 */
export type Selector<T, R> = (state: T) => R;

/** Store 介面 */
export interface Store<T> {
  /** 取得當前狀態 */
  getState: () => T;

  /** 更新狀態（部分更新） */
  setState: (partial: Partial<T> | ((state: T) => Partial<T>)) => void;

  /** 重設狀態至初始值 */
  reset: () => void;

  /** 訂閱狀態變更 */
  subscribe: (listener: Listener<T>) => () => void;

  /** 訂閱選取的狀態片段變更 */
  subscribeSelector: <R>(
    selector: Selector<T, R>,
    listener: (value: R, prevValue: R) => void,
    equalityFn?: (a: R, b: R) => boolean
  ) => () => void;
}

// ========================================================================
// Store Factory
// ========================================================================

/**
 * 建立一個新的 Store
 *
 * @param initialState - 初始狀態
 * @returns Store 實例
 *
 * @example
 * ```ts
 * interface CounterState {
 *   count: number;
 * }
 *
 * const counterStore = createStore<CounterState>({ count: 0 });
 *
 * // 訂閱變更
 * const unsubscribe = counterStore.subscribe((state) => {
 *   console.log('Count changed:', state.count);
 * });
 *
 * // 更新狀態
 * counterStore.setState({ count: 1 });
 *
 * // 使用函式更新
 * counterStore.setState((state) => ({ count: state.count + 1 }));
 *
 * // 取消訂閱
 * unsubscribe();
 * ```
 */
export function createStore<T extends object>(initialState: T): Store<T> {
  let state = { ...initialState };
  const listeners = new Set<Listener<T>>();

  const getState = (): T => state;

  const setState = (partial: Partial<T> | ((state: T) => Partial<T>)): void => {
    const prevState = state;
    const nextPartial = typeof partial === 'function' ? partial(state) : partial;

    // 只有在有實際變更時才更新
    const hasChanges = Object.keys(nextPartial).some(
      (key) => nextPartial[key as keyof T] !== state[key as keyof T]
    );

    if (!hasChanges) return;

    state = { ...state, ...nextPartial };

    // 通知所有監聽器
    listeners.forEach((listener) => {
      try {
        listener(state, prevState);
      } catch (error) {
        console.error('[Store] Listener error:', error);
      }
    });
  };

  const reset = (): void => {
    setState({ ...initialState });
  };

  const subscribe = (listener: Listener<T>): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const subscribeSelector = <R>(
    selector: Selector<T, R>,
    listener: (value: R, prevValue: R) => void,
    equalityFn: (a: R, b: R) => boolean = Object.is
  ): (() => void) => {
    let prevValue = selector(state);

    return subscribe((newState, _prevState) => {
      const newValue = selector(newState);
      if (!equalityFn(newValue, prevValue)) {
        const oldValue = prevValue;
        prevValue = newValue;
        listener(newValue, oldValue);
      }
    });
  };

  return {
    getState,
    setState,
    reset,
    subscribe,
    subscribeSelector,
  };
}

